import React from 'react'
import { Box, Link, Typography, SvgIcon, Popover, List, ListItem, ListItemText, Divider } from '@mui/material'
import Image from 'next/image'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic'
import { useLocale } from 'next-intl'

import LanguageCurrencyDropdownLogic from '../LanguageCurrencyDropdown'

import ClearTripLogo from '@/src/assets/png/ClearTripLogo.png'

interface CTHeaderProps {
    navLinks: { label: string; href: string; labelKey?: string; icon?: React.ReactNode; isActive?: boolean }[]
    options: {
        language: { code: string; label: string; Flag: React.ComponentType<any> }[]
        currency: { code: string; label: string; Flag: React.ComponentType<any>; symbol: string }[]
    }
    selected: {
        language: { code: string; label: string; Flag: React.ComponentType<any> }
        currency: { code: string; label: string; Flag: React.ComponentType<any>; symbol: string }
    }
    menuAnchors: {
        user: null | HTMLElement
    }
    onMenuOpen: (menu: 'user', event: React.MouseEvent<HTMLElement>) => void
    onMenuClose: (menu: 'user') => void
    onSelect: (type: 'language' | 'currency', value: any) => void
    onModalOpen: (modal: 'signIn') => void
    t: (key: string) => string
    tCurrency: (key: string) => string
    user: { signedIn: boolean; email?: string }
    userMenuItems: { label: string; href: string }[]
    onSignOut: () => void
    showLangCurrDropdown: boolean
    getNavIcon: (label: string) => React.ReactNode
    hideSignIn?: boolean
    showDeals?: boolean
}

const CTHeader: React.FC<CTHeaderProps> = ({
    navLinks,
    options,
    selected,
    menuAnchors,
    onMenuOpen,
    onMenuClose,
    onSelect,
    onModalOpen,
    t,
    tCurrency,
    user,
    userMenuItems,
    onSignOut,
    showLangCurrDropdown,
    getNavIcon,
    hideSignIn = false,
    showDeals = true
}) => {
    const locale = useLocale()

    return (
        <>
            <Box
                sx={{
                    width: '100%',
                    bgcolor: 'background.paper',
                    borderBottom: '1px solid',
                    borderColor: 'grey.300',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 3,
                    py: 1,
                    height: 60
                }}
            >
                {/* Left: Logo + Nav */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {/* Cleartrip Logo */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            height: 32
                        }}
                        onClick={() => (window.location.href = `/${locale}`)}
                    >
                        <Image
                            src={ClearTripLogo}
                            alt="Cleartrip"
                            height={32}
                            width={100}
                            style={{ objectFit: 'contain' }}
                            priority
                        />
                    </Box>

                    {/* Navigation Links */}
                    <Box component="nav" sx={{ display: 'flex', gap: 3 }}>
                        {navLinks.map(link => (
                            <Box
                                key={link.label}
                                component="div"
                                onClick={e => {
                                    e.preventDefault()
                                    window.location.href = link.href
                                }}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    fontSize: 14,
                                    fontWeight: 500,
                                    color: link.isActive ? 'link.main' : 'text.secondary',
                                    transition: 'color 0.2s',
                                    pb: 0.5,
                                    cursor: 'pointer'
                                }}
                            >
                                {getNavIcon(link.label)}
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* Right: Actions */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2.5
                    }}
                >
                    {/* Language/Currency Dropdown */}
                    {showLangCurrDropdown && (
                        <Box sx={{ mr: 1 }}>
                            <LanguageCurrencyDropdownLogic
                                selectedLanguage={selected.language}
                                selectedCurrency={selected.currency}
                                languageOptions={options.language}
                                currencyOptions={options.currency}
                                onLanguageSelect={lang => onSelect('language', lang)}
                                onCurrencySelect={curr => onSelect('currency', curr)}
                                t={t}
                                tCurrency={tCurrency}
                            />
                        </Box>
                    )}

                    {/* Deals: visible only on desired pages */}
                    {showDeals && (
                        <Link
                            href="/offers"
                            underline="none"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                fontSize: 14,
                                fontWeight: 500,
                                color: 'text.secondary',
                                '&:hover': { color: 'primary.main' },
                                transition: 'color 0.2s',
                                marginRight: '6px',
                                px: 0.7
                            }}
                        >
                            <Box sx={{ mr: 0.7, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <SvgIcon sx={{ width: 17, height: 17 }}>
                                    <svg width="17" height="17" fill="none" viewBox="0 0 24 24">
                                        <path
                                            d="M4 7V6a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v1"
                                            stroke="currentColor"
                                            strokeWidth="1.3"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        ></path>
                                        <rect
                                            width="20"
                                            height="13"
                                            x="2"
                                            y="7"
                                            rx="2"
                                            stroke="currentColor"
                                            strokeWidth="1.3"
                                        ></rect>
                                        <circle cx="17" cy="12" r="1" fill="currentColor"></circle>
                                    </svg>
                                </SvgIcon>
                            </Box>
                            {t('deals')}
                        </Link>
                    )}

                    {/* Support Link */}
                    <Link
                        href="/support"
                        underline="none"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            fontSize: 14,
                            fontWeight: 500,
                            color: 'text.secondary',
                            '&:hover': { color: 'primary.main' },
                            transition: 'color 0.2s'
                        }}
                    >
                        <HeadsetMicIcon sx={{ fontSize: 20 }} />
                        {t('support')}
                    </Link>

                    {/* User Menu / Sign In */}
                    {user.signedIn ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                gap: 0.5,
                                px: 1.5,
                                py: 0.75,
                                borderRadius: 1,
                                '&:hover': { bgcolor: 'grey.100' },
                                transition: 'background-color 0.2s'
                            }}
                            onClick={e => user.email && onMenuOpen('user', e)}
                        >
                            <PersonOutlineIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                            <Typography
                                sx={{
                                    fontWeight: 500,
                                    fontSize: 14,
                                    color: 'text.secondary',
                                    maxWidth: 120,
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {user.email?.split('@')[0] || 'User'}
                            </Typography>
                            {user.email && (
                                <SvgIcon sx={{ fontSize: 16, color: 'text.secondary' }}>
                                    <svg viewBox="0 0 24 24">
                                        <path d="M7 10l5 5 5-5" stroke="currentColor" strokeWidth="2" fill="none" />
                                    </svg>
                                </SvgIcon>
                            )}
                        </Box>
                    ) : !hideSignIn ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                gap: 0.5,
                                px: 1.5,
                                py: 0.75,
                                borderRadius: 1,
                                '&:hover': {
                                    bgcolor: 'primary.lighter',
                                    borderColor: 'primary.dark'
                                },
                                border: '1px solid',
                                borderColor: 'primary.main',
                                bgcolor: 'background.paper',
                                transition: 'all 0.2s'
                            }}
                            onClick={() => {
                                onModalOpen('signIn')
                            }}
                        >
                            <PersonOutlineIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                            <Typography
                                sx={{
                                    fontWeight: 500,
                                    fontSize: 14,
                                    color: 'primary.main'
                                }}
                            >
                                {t('signIn')}
                            </Typography>
                        </Box>
                    ) : null}

                    {/* User Dropdown Menu */}
                    <Popover
                        open={!!menuAnchors.user && user.signedIn}
                        anchorEl={menuAnchors.user}
                        onClose={() => onMenuClose('user')}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        PaperProps={{
                            sx: {
                                mt: 1,
                                borderRadius: 1,
                                minWidth: 200,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                border: '1px solid',
                                borderColor: 'grey.200'
                            }
                        }}
                    >
                        <List sx={{ p: 0 }}>
                            {userMenuItems.map((item, index) => (
                                <React.Fragment key={item.label}>
                                    <ListItem
                                        component="a"
                                        href={item.href}
                                        sx={{
                                            py: 1.25,
                                            px: 2,
                                            color: 'text.secondary',
                                            fontSize: 14,
                                            '&:hover': {
                                                bgcolor: 'grey.50',
                                                color: 'primary.main'
                                            },
                                            transition: 'all 0.2s',
                                            textDecoration: 'none'
                                        }}
                                    >
                                        <ListItemText
                                            primary={item.label}
                                            primaryTypographyProps={{
                                                fontSize: 14,
                                                fontWeight: 400
                                            }}
                                        />
                                    </ListItem>
                                    {index < userMenuItems.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}
                            <Divider />
                            <ListItem
                                onClick={onSignOut}
                                sx={{
                                    py: 1.25,
                                    px: 2,
                                    color: 'text.secondary',
                                    fontSize: 14,
                                    cursor: 'pointer',
                                    '&:hover': {
                                        bgcolor: 'grey.50',
                                        color: '#FF6B35'
                                    },
                                    transition: 'all 0.2s'
                                }}
                            >
                                <ListItemText
                                    primary={t('userMenu.signOut')}
                                    primaryTypographyProps={{
                                        fontSize: 14,
                                        fontWeight: 400
                                    }}
                                />
                            </ListItem>
                        </List>
                    </Popover>
                </Box>
            </Box>
        </>
    )
}

export { CTHeader }
export type { CTHeaderProps }
